/*
 * /guardian-sign/:token — the parent's sign page for a 16–17 teen self-holder
 * (VITE_TEEN_SELF_HOLDER_ENABLED; design MY-DESIGN-JUNIOR-SELF-HOLDER-1020
 * §4.3.4; my-api PR2 contract: services/my-api/src/guardian-sign.ts + README
 * routes + docs/current/TEEN-GUARDIAN-EMAILS-CONTRACT-V1.md). PUBLIC: matched
 * before the auth gate in App.tsx, like /join/:slug.
 *
 *   1. Context — "{Teen} has a ticket for {event}; they're under 18 on race
 *      day, so a parent or guardian must sign." (GET /guardian-sign/{token})
 *   2. Verify  — the parent types their email → POST …/verify step 1 (must be
 *      the address the request went to; my-api prepares the account) → the
 *      existing native email-code sign-in (the Login component, prefilled;
 *      the token rides a one-hour stash across the sign-in exactly like
 *      /join/confirm) → POST …/verify step 2 with the verified ID token →
 *      a 30-minute signing session.
 *   3. Sign    — the event's waiver read with the WaiverFlow model helpers and
 *      guardian wording (docIntro / signChrome / fillParticipant / sections),
 *      then typed name, relationship, the "I'm 18 or over and the parent /
 *      legal guardian" declaration and the two OPTIONAL, UNTICKED brand-scoped
 *      boxes (PROGRAMME-RULE-MINOR-MARKETING-CONSENT-VIA-PARENT-V1). The boxes
 *      are sent only as the parent left them — false unless THEY ticked one.
 *   4. Done    — "{Teen}'s ticket will be ready once their other steps are
 *      complete."
 *
 * Errors: 410 (link no longer active — reminders rotate the token, so ask the
 * teen to resend from Get ready; boardroom condition a), 422 minor_signer,
 * 503, 429. VERIFY never reveals whether a parent account exists: every
 * verify failure shows ONE neutral copy except email_mismatch (boardroom
 * condition c). The token is never rendered, logged or stored beyond the
 * one-hour sign-in stash. The page carries <meta name="referrer"
 * content="no-referrer"> (infra's ask; my-api also sends Referrer-Policy).
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { clearSession, loadSession } from '../auth/session'
import { normalizeEmail } from '../auth/normalizeEmail'
import { Brand, Card } from '../components/ui'
import {
  fetchGuardianSign, guardianSignSubmit, guardianSignVerifyEmail, guardianSignVerifySession, myApiBase, MyApiError,
  peekPendingGuardianSign, storePendingGuardianSign, takePendingGuardianSign, type GuardianSignView,
} from '../data/myApi'
import { canonicalTemplateText, hashWaiverTemplate, tryWaiverTemplateViaBff, type WaiverTemplate } from '../data/waiverTemplate'
import { waiverSnapshotReceiptEnabled } from '../lib/waiverSnapshotFlag'
import { docIntro, fillParticipant, sectionKey, signChrome, type ConsentState } from './waiver/waiverModel'
import { useLocale, type TranslateFn } from '../i18n/useLocale'
import { Login } from './Login'
import { EmailHiddenCharNotice, useCleanEmail } from '../components/EmailHiddenCharNotice'

// ---------------------------------------------------------------------------
// Referrer policy (infra's ask): a meta tag while this page is mounted.
// ---------------------------------------------------------------------------

const REFERRER_META_ID = 'guardian-sign-referrer'

/** Insert <meta name="referrer" content="no-referrer"> once (idempotent). */
export function ensureNoReferrerMeta(doc: Document = document): void {
  if (doc.getElementById(REFERRER_META_ID)) return
  const m = doc.createElement('meta')
  m.id = REFERRER_META_ID
  m.name = 'referrer'
  m.content = 'no-referrer'
  doc.head.appendChild(m)
}

// ---------------------------------------------------------------------------

type Phase =
  | { kind: 'loading' }
  | { kind: 'context' }
  | { kind: 'verify' }
  | { kind: 'signin'; email: string }
  | { kind: 'verifying' }
  | { kind: 'sign'; session: string }
  | { kind: 'done' }
  | { kind: 'expired' }
  | { kind: 'minor_signer' }
  | { kind: 'unavailable' }
  | { kind: 'rate_limited'; seconds: number }
  | { kind: 'waiver_unavailable' }

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
type Relationship = 'parent' | 'legal_guardian'

const labelStyle: CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', margin: '12px 0 5px' }
const inputStyle: CSSProperties = {
  width: '100%', minHeight: 44, border: '1px solid var(--hairline-14)', borderRadius: 10, padding: '0 12px',
  fontSize: 15, background: 'var(--bg-frame)', color: 'var(--text)', fontFamily: 'inherit', boxSizing: 'border-box',
}
const checkRowStyle: CSSProperties = { display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.45, cursor: 'pointer', marginTop: 12 }
const checkboxStyle: CSSProperties = { width: 18, height: 18, marginTop: 1, flex: 'none', accentColor: 'var(--accent)' }
const primaryBtn = (ok: boolean): CSSProperties => ({
  width: '100%', minHeight: 46, marginTop: 16, border: 0, borderRadius: 'var(--r-btn)', background: 'var(--accent)',
  color: 'var(--accent-ink, #03201c)', fontFamily: 'inherit', fontSize: 15, fontWeight: 800,
  ...(ok ? { cursor: 'pointer' } : { opacity: 0.4, cursor: 'not-allowed' }),
})
const errStyle: CSSProperties = { fontSize: 12.5, color: 'var(--warn)', fontWeight: 700, margin: '10px 0 0', lineHeight: 1.45 }
const pStyle: CSSProperties = { fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-secondary)', margin: '8px 0 0' }

function retrySeconds(e: MyApiError): number {
  const v = e.body?.retry_after
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) && n > 0 ? Math.ceil(n) : 60
}

/** The {brand} for the consent boxes: the waiver's organiser (the event's
 *  org unit name on the resolved template), else the event name. */
function brandOf(template: WaiverTemplate | null, view: GuardianSignView | null, t: TranslateFn): string {
  return template?.organiser?.trim() || view?.event_name?.trim() || t('guardianSign.brandFallback')
}

export function GuardianSign({
  token, fetchImpl, base: baseProp, renderSignIn,
}: {
  token: string
  fetchImpl?: typeof fetch
  base?: string | null
  /** The native email-code sign-in (default: the app's Login). Test seam. */
  renderSignIn?: () => ReactNode
}) {
  const { t } = useLocale()
  const f = fetchImpl ?? fetch
  const base = baseProp === undefined ? myApiBase() : baseProp
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })
  const [view, setView] = useState<GuardianSignView | null>(null)
  const [eventId, setEventId] = useState<string | null>(null)
  const [template, setTemplate] = useState<WaiverTemplate | null>(null)
  const [email, setEmail, emailHidden] = useCleanEmail()
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Sign form — the two marketing boxes start UNTICKED and only the parent's
  // own click changes them.
  const [consents, setConsents] = useState<ConsentState>({})
  const [typedName, setTypedName] = useState('')
  const [relationship, setRelationship] = useState<'' | Relationship>('')
  const [declares, setDeclares] = useState(false)
  const [childNews, setChildNews] = useState(false)
  const [parentNews, setParentNews] = useState(false)
  const [signError, setSignError] = useState<string | null>(null)
  const started = useRef(false)

  useEffect(() => { ensureNoReferrerMeta() }, [])

  const teen = (view?.child_display_name ?? '').trim()
  const teenOr = teen || t('guardianSign.teenFallback')

  /** Shared non-verify error routing (GET / sign). */
  const routeError = (e: unknown): boolean => {
    if (!(e instanceof MyApiError)) return false
    if (e.status === 410 || e.code === 'link_expired') { setPhase({ kind: 'expired' }); return true }
    if (e.status === 429) { setPhase({ kind: 'rate_limited', seconds: retrySeconds(e) }); return true }
    if (e.status === 503) { setPhase({ kind: 'unavailable' }); return true }
    return false
  }

  /** Verify step 2: the verified ID token → the signing session. */
  const runStep2 = async (idToken: string, idFromView: string | null) => {
    if (!base) { setPhase({ kind: 'unavailable' }); return }
    setPhase({ kind: 'verifying' })
    try {
      const s = await guardianSignVerifySession(base, token, idToken, f)
      const ev = (s.event_id ?? '').trim() || idFromView
      setEventId(ev)
      if (!ev) { setPhase({ kind: 'waiver_unavailable' }); return }
      try {
        const r = await tryWaiverTemplateViaBff(ev, idToken, f, base)
        const tpl = r.handled ? r.template : null
        if (!tpl) { setPhase({ kind: 'waiver_unavailable' }); return }
        setTemplate(tpl)
      } catch {
        setPhase({ kind: 'waiver_unavailable' })
        return
      }
      setPhase({ kind: 'sign', session: s.session })
    } catch (e) {
      if (e instanceof MyApiError && e.code === 'already_signed') { setPhase({ kind: 'done' }); return }
      if (e instanceof MyApiError && e.reason === 'minor_signer') { setPhase({ kind: 'minor_signer' }); return }
      if (e instanceof MyApiError && (e.status === 403 || e.code === 'email_mismatch')) {
        setVerifyError(t('guardianSign.verify.mismatch'))
      } else {
        // ONE neutral copy for every other verify outcome (boardroom
        // condition c — never whether a parent account exists).
        setVerifyError(t('guardianSign.verify.neutral', { teen: teenOr }))
      }
      setPhase({ kind: 'verify' })
    }
  }

  // Load the context; resume verify step 2 after the sign-in round trip.
  useEffect(() => {
    if (started.current) return
    started.current = true
    // The sign-in stash is consumed on EVERY load (one-shot — App.tsx only
    // peeks it to bring a detoured sign-in back here), so it can never loop.
    const resume = peekPendingGuardianSign() === token
    takePendingGuardianSign()
    if (!base) { setPhase({ kind: 'unavailable' }); return }
    fetchGuardianSign(base, token, f)
      .then((v) => {
        setView(v)
        const ev = (v.event_id ?? '').trim() || null
        setEventId(ev)
        if (v.state === 'signed') { setPhase({ kind: 'done' }); return }
        const session = loadSession()
        if (session && resume) {
          void runStep2(session.idToken, ev)
          return
        }
        setPhase({ kind: 'context' })
      })
      .catch((e) => {
        if (!routeError(e)) setPhase({ kind: 'unavailable' })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submitEmail = async () => {
    const typed = email.trim()
    if (!EMAIL_RE.test(typed) || busy || !base) return
    setBusy(true)
    setVerifyError(null)
    try {
      await guardianSignVerifyEmail(base, token, typed, f)
    } catch (e) {
      setBusy(false)
      if (e instanceof MyApiError && (e.status === 403 || e.code === 'email_mismatch')) {
        setVerifyError(t('guardianSign.verify.mismatch'))
      } else {
        setVerifyError(t('guardianSign.verify.neutral', { teen: teenOr }))
      }
      return
    }
    setBusy(false)
    const session = loadSession()
    const sessionEmail = typeof session?.claims?.email === 'string' ? session.claims.email : ''
    if (session && sessionEmail && normalizeEmail(sessionEmail) === normalizeEmail(typed)) {
      // Already signed in as this parent — straight to step 2.
      void runStep2(session.idToken, eventId)
      return
    }
    // Native email-code sign-in for THIS address. A different account signed
    // in on this browser is signed out first (it can't verify this request).
    storePendingGuardianSign(token)
    if (session) clearSession()
    try {
      const path = `/guardian-sign/${encodeURIComponent(token)}`
      const q = new URLSearchParams({ email: typed, returnTo: path })
      window.history.replaceState(window.history.state, '', `${path}?${q.toString()}`)
    } catch {
      /* the Login email field is then simply empty */
    }
    setPhase({ kind: 'signin', email: typed })
  }

  // Required sections must be ticked; read-only (no consent) sections are
  // accepted by signing; optional ticks are the parent's choice.
  const docs = template?.documents ?? []
  const requiredLeft = docs.reduce((n, d) => n + d.sections.filter((s, i) => s.required && !consents[sectionKey(d.id, i)]).length, 0)
  const acceptedSections = (): string[] => {
    const out: string[] = []
    for (const d of docs) {
      d.sections.forEach((s, i) => {
        const tickable = s.required || !!s.consent
        if (!tickable || consents[sectionKey(d.id, i)]) out.push(s.name)
      })
    }
    return [...new Set(out)]
  }
  const signOk = requiredLeft === 0 && typedName.trim() !== '' && relationship !== '' && declares

  const submitSign = async () => {
    if (phase.kind !== 'sign' || !signOk || busy || !base) return
    setBusy(true)
    setSignError(null)
    try {
      await guardianSignSubmit(base, token, {
        session: phase.session,
        typedName: typedName.trim(),
        relationship,
        declaresAdult: true,
        accepted_sections: acceptedSections(),
        // Exactly what the parent left the boxes at — false unless ticked.
        consent_child_news: childNews === true,
        consent_parent_news: parentNews === true,
        // WAIVER SIGNING SNAPSHOT (flag on only): pin the template this page
        // showed; my-api refuses (409 template_changed) when it moved.
        ...(waiverSnapshotReceiptEnabled() && template
          ? { template_hash: hashWaiverTemplate(canonicalTemplateText(template)) }
          : {}),
      }, f)
      setPhase({ kind: 'done' })
    } catch (e) {
      if (e instanceof MyApiError && e.code === 'already_signed') { setPhase({ kind: 'done' }); return }
      if (waiverSnapshotReceiptEnabled() && e instanceof MyApiError && e.code === 'template_changed') {
        // Nothing was signed: re-resolve the waiver, reset the ticks, read again.
        const idToken = loadSession()?.idToken
        if (eventId && idToken && base) {
          try {
            const r = await tryWaiverTemplateViaBff(eventId, idToken, f, base)
            if (r.handled && r.template) setTemplate(r.template)
          } catch { /* keep the page; the notice still asks for a re-read */ }
        }
        setConsents({})
        setSignError(t('waiver.templateChanged'))
        return
      }
      if (e instanceof MyApiError && (e.status === 401 || e.code === 'session_invalid' || e.code === 'not_verified')) {
        setVerifyError(t('guardianSign.sign.sessionExpired'))
        setPhase({ kind: 'verify' })
        return
      }
      if (routeError(e)) return
      if (e instanceof MyApiError && e.code === 'required_sections_not_accepted') setSignError(t('guardianSign.sign.sectionsMissing'))
      else if (e instanceof MyApiError && e.code === 'adult_declaration_required') setSignError(t('guardianSign.sign.declarationRequired'))
      else if (e instanceof MyApiError && e.serverMessage) setSignError(e.serverMessage)
      else setSignError(t('guardianSign.sign.error'))
    } finally {
      setBusy(false)
    }
  }

  if (phase.kind === 'signin') {
    return (
      <div data-testid="guardian-sign" data-phase="signin">
        {renderSignIn ? renderSignIn() : <Login />}
      </div>
    )
  }

  const brand = brandOf(template, view, t)
  const guardianSubject = { kind: 'guardian' as const, who: teenOr }
  const chrome = signChrome(guardianSubject, teenOr, t)

  return (
    <div data-testid="guardian-sign" data-phase={phase.kind} style={{ maxWidth: 560, margin: '24px auto', padding: '0 16px' }}>
      <div style={{ marginBottom: 14 }}><Brand /></div>
      <Card>
        <div style={{ padding: 20 }}>
          <h1 style={{ fontSize: 19, margin: 0 }}>{t('guardianSign.title')}</h1>

          {phase.kind === 'loading' && <p role="status" style={pStyle}>{t('guardianSign.loading')}</p>}

          {(phase.kind === 'context' || phase.kind === 'verify' || phase.kind === 'verifying') && view && (
            <p data-testid="guardian-sign-context" style={pStyle}>
              {t('guardianSign.context', { teen: teenOr, event: view.event_name })}
              {view.ticket_type ? ` ${t('guardianSign.contextTicket', { ticket: view.ticket_type })}` : ''}
            </p>
          )}

          {phase.kind === 'context' && (
            <button type="button" data-testid="guardian-sign-start" onClick={() => setPhase({ kind: 'verify' })} style={primaryBtn(true)}>
              {t('guardianSign.start')}
            </button>
          )}

          {phase.kind === 'verify' && (
            <form onSubmit={(e) => { e.preventDefault(); void submitEmail() }} noValidate>
              <p style={pStyle}>{t('guardianSign.verify.body')}</p>
              <label style={labelStyle} htmlFor="gs-email">{t('guardianSign.verify.email')}</label>
              <input
                id="gs-email"
                data-testid="guardian-sign-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
              />
              <EmailHiddenCharNotice show={emailHidden} testid="guardian-sign-email-hidden-char" />
              {verifyError && <p data-testid="guardian-sign-verify-error" role="alert" style={errStyle}>{verifyError}</p>}
              <button type="submit" data-testid="guardian-sign-verify" aria-disabled={!EMAIL_RE.test(email.trim()) || busy} style={primaryBtn(EMAIL_RE.test(email.trim()) && !busy)}>
                {busy ? t('guardianSign.working') : t('guardianSign.verify.submit')}
              </button>
            </form>
          )}

          {phase.kind === 'verifying' && <p role="status" style={pStyle}>{t('guardianSign.verifying')}</p>}

          {phase.kind === 'sign' && template && (
            <div data-testid="guardian-sign-form">
              <p style={pStyle}>{chrome.intro}</p>
              {docs.map((d) => (
                <section key={d.id} data-testid="guardian-sign-doc" style={{ marginTop: 18 }}>
                  <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>{fillParticipant(d.title, teenOr)} <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{d.version}</span></h2>
                  <p style={{ ...pStyle, fontSize: 12 }}>{docIntro(d, guardianSubject, template.organiser, false, t)}</p>
                  {d.sections.map((s, i) => {
                    const k = sectionKey(d.id, i)
                    const tickable = s.required || !!s.consent
                    return (
                      <div key={k} data-testid="guardian-sign-section" data-section={s.name} style={{ marginTop: 12, padding: '10px 12px', border: '1px solid var(--hairline-08)', borderRadius: 10 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                          <strong style={{ flex: 1, fontSize: 13.5 }}>{fillParticipant(s.name, teenOr)}</strong>
                          <span className="mono" style={{ fontSize: 9, letterSpacing: '.08em', color: 'var(--text-muted)' }}>
                            {s.required ? t('waiver.tag.must') : s.consent ? t('waiver.tag.optional') : t('waiver.tag.signed')}
                          </span>
                        </div>
                        {s.clauses.map((c) => (
                          <p key={c.num} style={{ fontSize: 12.5, lineHeight: 1.55, margin: '6px 0 0', paddingInlineStart: c.sub ? 18 : 0 }}>
                            <span style={{ color: 'var(--text-muted)', marginInlineEnd: 6 }}>{c.num}</span>{fillParticipant(c.text, teenOr)}
                          </p>
                        ))}
                        {s.note && <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '6px 0 0' }}>{fillParticipant(s.note, teenOr)}</p>}
                        {tickable && (
                          <label style={checkRowStyle}>
                            <input
                              type="checkbox"
                              data-testid="guardian-sign-section-tick"
                              checked={!!consents[k]}
                              onChange={(e) => setConsents((c) => ({ ...c, [k]: e.target.checked }))}
                              style={checkboxStyle}
                            />
                            <span>{fillParticipant(s.consent || s.name, teenOr)}</span>
                          </label>
                        )}
                      </div>
                    )
                  })}
                </section>
              ))}

              {/* GUARDIAN DECLARATION (flag on; boardroom ruling 4): the
                  settings declaration this parent signs. */}
              {template?.guardianDeclaration && (
                <section data-testid="guardian-sign-declaration" style={{ marginTop: 18 }}>
                  <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>{t('waiver.guardianDeclaration.title')}</h2>
                  <p style={{ fontSize: 13, lineHeight: 1.5, margin: 0 }}>{fillParticipant(template.guardianDeclaration, teenOr)}</p>
                </section>
              )}
              <h2 style={{ fontSize: 15, margin: '22px 0 0' }}>{chrome.heading}</h2>
              <label style={labelStyle} htmlFor="gs-name">{chrome.label}</label>
              <input id="gs-name" data-testid="guardian-sign-name" value={typedName} onChange={(e) => setTypedName(e.target.value)} autoComplete="name" style={inputStyle} />
              <label style={labelStyle} htmlFor="gs-rel">{t('guardianSign.relationship')}</label>
              <select id="gs-rel" data-testid="guardian-sign-relationship" value={relationship} onChange={(e) => setRelationship(e.target.value as typeof relationship)} style={inputStyle}>
                <option value="">{t('teenGuardian.field.relationship.choose')}</option>
                <option value="parent">{t('teenGuardian.relationship.parent')}</option>
                <option value="legal_guardian">{t('teenGuardian.relationship.legal_guardian')}</option>
              </select>
              <label style={checkRowStyle}>
                <input type="checkbox" data-testid="guardian-sign-declare" checked={declares} onChange={(e) => setDeclares(e.target.checked)} style={checkboxStyle} />
                <span>{t('guardianSign.declare', { teen: teenOr })}</span>
              </label>

              <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--hairline-07)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('guardianSign.consent.heading')}</div>
                <label style={checkRowStyle}>
                  <input type="checkbox" data-testid="guardian-sign-consent-child" checked={childNews} onChange={(e) => setChildNews(e.target.checked)} style={checkboxStyle} />
                  <span>{t('guardianSign.consent.child', { child: teenOr, brand })}</span>
                </label>
                <label style={checkRowStyle}>
                  <input type="checkbox" data-testid="guardian-sign-consent-parent" checked={parentNews} onChange={(e) => setParentNews(e.target.checked)} style={checkboxStyle} />
                  <span>{t('guardianSign.consent.parent', { brand })}</span>
                </label>
              </div>

              {!signOk && (
                <p data-testid="guardian-sign-block" style={{ ...errStyle, color: 'var(--text-muted)', fontWeight: 600 }}>
                  {requiredLeft > 0
                    ? t('guardianSign.block.sections', { n: requiredLeft })
                    : !typedName.trim()
                      ? t('guardianSign.block.name')
                      : !relationship
                        ? t('guardianSign.block.relationship')
                        : t('guardianSign.block.declare')}
                </p>
              )}
              {signError && <p data-testid="guardian-sign-error" role="alert" style={errStyle}>{signError}</p>}
              <button type="button" data-testid="guardian-sign-submit" onClick={() => void submitSign()} aria-disabled={!signOk || busy} style={primaryBtn(signOk && !busy)}>
                {busy ? t('guardianSign.working') : t('guardianSign.sign.submit')}
              </button>
            </div>
          )}

          {phase.kind === 'done' && (
            <div data-testid="guardian-sign-done" role="status">
              <p style={{ ...pStyle, fontWeight: 700, color: 'var(--text)' }}>{t('guardianSign.done.title')}</p>
              <p style={pStyle}>{t('guardianSign.done.body', { teen: teenOr })}</p>
            </div>
          )}

          {phase.kind === 'expired' && (
            <p data-testid="guardian-sign-expired" role="alert" style={pStyle}>
              {teen ? t('guardianSign.expired', { teen }) : t('guardianSign.expiredNoName')}
            </p>
          )}
          {phase.kind === 'minor_signer' && (
            <p data-testid="guardian-sign-minor" role="alert" style={pStyle}>{t('guardianSign.minorSigner', { teen: teenOr })}</p>
          )}
          {phase.kind === 'unavailable' && (
            <p data-testid="guardian-sign-unavailable" role="alert" style={pStyle}>{t('guardianSign.unavailable')}</p>
          )}
          {phase.kind === 'rate_limited' && (
            <p data-testid="guardian-sign-ratelimited" role="alert" style={pStyle}>
              {t('guardianSign.rateLimited', { minutes: Math.max(1, Math.ceil(phase.seconds / 60)) })}
            </p>
          )}
          {phase.kind === 'waiver_unavailable' && (
            <p data-testid="guardian-sign-waiver-unavailable" role="alert" style={pStyle}>{t('guardianSign.waiverUnavailable', { teen: teenOr })}</p>
          )}
        </div>
      </Card>
    </div>
  )
}
